/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { carouselItems as initialCarouselItems } from '../data/carouselData';
import { HomepageHeroImage, homepageHeroImages as initialHeroImages } from '../data/homepageImages';
import { BACKEND_CONFIG, FIREBASE_CONFIG } from '../config';
import { firestoreConfigProvider } from './persistence/FirestoreConfigProvider';
import { deleteDoc, doc } from 'firebase/firestore';
import { db } from './firebaseClient';
import { HeroConfig, CarouselItem } from '@shared/types';

const CAROUSEL_STORAGE_KEY = 'codina_carousel_items';
const HERO_CONFIG_STORAGE_KEY = 'codina_hero_config';

class HomeService {
    private carouselItems: CarouselItem[] = [];
    private heroImages: HomepageHeroImage[] = [];
    private heroConfig: HeroConfig = {
        firstImageId: 'elegant-1',
        secondImageId: 'elegant-stairs'
    };
    private initialized = false;

    constructor() {
        this.heroImages = [...initialHeroImages];
    }

    private async loadData() {
        if (this.initialized) return;

        if (FIREBASE_CONFIG.USE_FIREBASE_CONFIG_STORAGE) {
            try {
                const cItems = await firestoreConfigProvider.getCarouselItems();
                const hConfig = await firestoreConfigProvider.getHeroConfig();
                
                this.carouselItems = cItems.length ? cItems : [...initialCarouselItems];
                this.heroConfig = hConfig ? hConfig : this.heroConfig;
            } catch (e) {
                console.error('Failed to load home data from Firestore', e);
                this.loadFromStorage();
            }
        } else if (BACKEND_CONFIG.USE_EXTERNAL_BACKEND) {
            try {
                const [carouselRes, heroRes] = await Promise.all([
                    fetch(`${BACKEND_CONFIG.BACKEND_URL}/api/v1/home/carousel`),
                    fetch(`${BACKEND_CONFIG.BACKEND_URL}/api/v1/home/hero`)
                ]);
                const cItems = await carouselRes.json();
                const hConfig = await heroRes.json();
                this.carouselItems = cItems.length ? cItems : [...initialCarouselItems];
                this.heroConfig = hConfig.firstImageId ? hConfig : this.heroConfig;
            } catch (e) {
                console.error('Failed to load home data from backend', e);
                this.loadFromStorage();
            }
        } else {
            this.loadFromStorage();
        }
        this.initialized = true;
    }

    private loadFromStorage() {
        const savedCarousel = localStorage.getItem(CAROUSEL_STORAGE_KEY);
        if (savedCarousel) {
            try {
                this.carouselItems = JSON.parse(savedCarousel);
            } catch (e) {
                this.carouselItems = [...initialCarouselItems];
            }
        } else {
            this.carouselItems = [...initialCarouselItems];
        }

        const savedConfig = localStorage.getItem(HERO_CONFIG_STORAGE_KEY);
        if (savedConfig) {
            try {
                this.heroConfig = JSON.parse(savedConfig);
            } catch (e) { }
        }
    }

    private async saveCarousel() {
        if (FIREBASE_CONFIG.USE_FIREBASE_CONFIG_STORAGE) {
            try {
                await firestoreConfigProvider.saveCarouselItems(this.carouselItems);
            } catch (e) {
                console.error('Failed to save carousel to Firestore', e);
            }
        } else if (BACKEND_CONFIG.USE_EXTERNAL_BACKEND) {
            try {
                await fetch(`${BACKEND_CONFIG.BACKEND_URL}/api/v1/home/carousel`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(this.carouselItems)
                });
            } catch (e) {
                console.error('Failed to save carousel to backend', e);
            }
        } else {
            localStorage.setItem(CAROUSEL_STORAGE_KEY, JSON.stringify(this.carouselItems));
        }
    }

    private async saveHeroConfig() {
        if (FIREBASE_CONFIG.USE_FIREBASE_CONFIG_STORAGE) {
            try {
                await firestoreConfigProvider.saveHeroConfig(this.heroConfig);
            } catch (e) {
                console.error('Failed to save hero config to Firestore', e);
            }
        } else if (BACKEND_CONFIG.USE_EXTERNAL_BACKEND) {
            try {
                await fetch(`${BACKEND_CONFIG.BACKEND_URL}/api/v1/home/hero`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(this.heroConfig)
                });
            } catch (e) {
                console.error('Failed to save hero config to backend', e);
            }
        } else {
            localStorage.setItem(HERO_CONFIG_STORAGE_KEY, JSON.stringify(this.heroConfig));
        }
    }

    async getCarouselItems(): Promise<CarouselItem[]> {
        await this.loadData();
        return this.carouselItems;
    }

    async addCarouselItem(item: Omit<CarouselItem, 'id'>): Promise<CarouselItem> {
        await this.loadData();
        const newItem = { ...item, id: `custom-${Date.now()}` };
        this.carouselItems.push(newItem);
        await this.saveCarousel();
        return newItem;
    }

    async updateCarouselItem(updatedItem: CarouselItem): Promise<void> {
        await this.loadData();
        const index = this.carouselItems.findIndex(item => item.id === updatedItem.id);
        if (index !== -1) {
            this.carouselItems[index] = updatedItem;
            await this.saveCarousel();
        }
    }

    async deleteCarouselItem(id: string): Promise<void> {
        await this.loadData();
        this.carouselItems = this.carouselItems.filter(item => item.id !== id);
        
        if (FIREBASE_CONFIG.USE_FIREBASE_CONFIG_STORAGE) {
            try {
                await deleteDoc(doc(db, 'config_carousel', id));
            } catch (e) {
                console.error('Failed to delete carousel doc in Firestore', e);
            }
        } else {
            await this.saveCarousel();
        }
    }

    async getHeroImages(): Promise<HomepageHeroImage[]> {
        return this.heroImages;
    }

    async getHeroConfig(): Promise<HeroConfig> {
        await this.loadData();
        return this.heroConfig;
    }

    async updateHeroConfig(config: HeroConfig): Promise<void> {
        await this.loadData();
        this.heroConfig = config;
        await this.saveHeroConfig();
    }

    async getSelectedHeroImages(): Promise<{ first: string, second: string }> {
        await this.loadData();
        const first = this.heroImages.find(img => img.id === this.heroConfig.firstImageId)?.url || initialHeroImages[0].url;
        const second = this.heroImages.find(img => img.id === this.heroConfig.secondImageId)?.url || initialHeroImages[1].url;
        return { first, second };
    }
}

export const homeService = new HomeService();
